<template>
  <div
    ref="tooltipContainer"
    data-samora
    class="sam-tooltip"
  >
    <SamIcon
      v-if="anchorName === 'self'"
      ref="tooltipIcon"
      class="sam-tooltip__icon"
      :size="iconSize"
      icon="information-circle"
    />
    <div
      ref="tooltip"
      class="sam-tooltip__tooltip"
      v-html="message"
    />
  </div>
</template>
<script>
import { computePosition, flip, shift, offset, autoUpdate } from '@floating-ui/dom';
import SamIcon, { iconSizesToPxMapper } from '../SamIcon/SamIcon.vue';
import SAMORA_SIZES from '../../../utils/constants/samora-sizes.enum';

export default {
  name: 'SamTooltip',
  components: {
    SamIcon,
  },
  props: {
    /**
     * The message to be displayed on the Tooltip. Html can be passed but beware of the content (we use v-html)
     */
    message: {
      type: String,
      required: true,
      default: '',
    },
    /**
     * The Tooltip's display position in realtion with it's trigger
     * @values top, right, bottom, left
     */
    position: {
      type: String,
      required: false,
      default: 'top',
      validator(pos) {
        return ['top', 'right', 'bottom', 'left'].includes(pos);
      },
    },
    /**
     * ID of the element which will trigger the Tooltip
     */
    anchorName: {
      type: String,
      required: false,
      default: 'self',
    },
    /**
     * The icon size. It can be any of the following: xs, sm, md, lg or any Integer number.
     * @values xs, sm, md, lg
     */
    iconSize: {
      type: [String, Number],
      required: false,
      default: SAMORA_SIZES.MEDIUM,
      // eslint-disable-next-line max-len
      validator: (val) =>
        Number.isInteger(val) || Array.from(iconSizesToPxMapper.keys()).includes(val),
    },
  },
  data() {
    return {
      activatorElement: null,
      tooltipElement: null,
      eventHandlers: [
        ['mouseenter', this.show],
        ['mouseleave', this.hide],
        ['focus', this.show],
        ['blur', this.hide],
      ],
      dismantle: null,
    };
  },
  mounted() {
    this.tooltipElement = this.$refs.tooltip;
    this.activatorElement =
      this.anchorName !== 'self'
        ? document.querySelector(`#${this.anchorName}`)
        : this.$refs.tooltipIcon.$el;

    this.dismantle = autoUpdate(this.activatorElement, this.tooltipElement, this.update);

    this.eventHandlers.forEach(([event, listener]) => {
      this.activatorElement.addEventListener(event, listener);
    });
  },
  beforeDestroy() {
    this.eventHandlers.forEach(([event, listener]) => {
      this.activatorElement.removeEventListener(event, listener);
    });

    this.dismantle();
  },
  methods: {
    update() {
      computePosition(this.activatorElement, this.tooltipElement, {
        placement: this.position,
        strategy: 'fixed',
        middleware: [offset(8), flip(), shift({ padding: 4 })],
      }).then(({ x, y }) => {
        Object.assign(this.tooltipElement.style, {
          left: `${x}px`,
          top: `${y}px`,
        });
      });
    },
    show() {
      this.tooltipElement.style.display = 'block';
      this.update();
    },
    hide() {
      this.tooltipElement.style.display = 'none';
    },
  },
};
</script>
<style lang="scss" scoped>
.sam-tooltip {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: $elevation--2;
  color: $informative--main;

  &__tooltip {
    @extend .px-2, .py-1, .text--caption;
    display: none;
    max-width: 260px;
    overflow-wrap: break-word;
    text-wrap: normal;
    white-space: pre-line;
    width: max-content;
    position: fixed;
    top: 0;
    left: 0;
    color: $pure--white;
    background-color: $neutral--90;
    border-radius: $size--1;
  }

  &:hover {
    color: $informative--hover;
  }
}
</style>
